import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Backdrop, Box, CircularProgress } from '@mui/material';
import { Button } from '@workboard/wobo-ui';
import {
  FinancialsChartModal,
  FinancialsService,
  FinancialsObjectType,
  WOBO_APP_IDS,
  ExcelIntegrationModal,
  FinancialType,
  resyncFinancialIntegration,
  trackFinancialsEvent,
  hasExcelIntegrationDataChanged,
  FINANCIALS_ACTIONS,
  FINANCIALS_LOCATIONS,
  FINANCIALS_OBJECTS,
  FINANCIALS_SOURCES,
} from '../../../../apps/financials';
import { KpiService } from '../../../../apps/financials/services/kpi.service';
import type { FinancialsConfigureState } from '../../../../apps/financials/services/financials.service';
import { krdetailConstants } from '../../../../constants/krdetails.constants';
import { transformDataPointValues } from '../../../../apps/financials/helpers/transformDataPointValues';

const ExcelIcon = () => (
  <span
    className="icon icon-excel-logo-14px"
    style={{
      display: 'inline-block',
      width: 18,
      height: 18,
      transform: 'scale(1.25)',
    }}
  />
);

interface OkrFinancialsProps {
  goalId: string;
}

const OkrFinancials = ({ goalId }: OkrFinancialsProps) => {
  const { t } = useTranslation();
  const [entityId, setEntityId] = useState<number | null>(null);
  const [kpiIntegrationData, setKpiIntegrationData] = useState<KpiData | null>(null);
  const [showExcelIntegrationModal, setShowExcelIntegrationModal] = useState(false);
  const [financialsConfigureState, setFinancialsConfigureState] = useState<
    FinancialsConfigureState | undefined
  >(undefined);
  const [externalCostBudgetResources, setExternalCostBudgetResources] = useState<{
    budget?: number;
    resources?: number;
    runningCost?: number;
  } | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshTrigger, _setRefreshTrigger] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);

  useEffect(() => {
    const financialsService = new FinancialsService();

    const fetchFinancialEntity = async () => {
      setIsLoading(true);
      try {
        const response = await financialsService.getEntityByObject({
          objectType: FinancialsObjectType.OKR,
          objectId: Number(goalId),
        });
        const financialEntityId = response?.data?.financial?.id;
        if (financialEntityId) {
          setEntityId(financialEntityId);
          const integrationInfo = await KpiService.getIntegrationInfoForObject({
            integrationType: krdetailConstants.EXCEL_ID,
            objectType: 'financial',
            objectId: financialEntityId,
          });
          setKpiIntegrationData(integrationInfo);
          setExternalCostBudgetResources(
            transformDataPointValues(integrationInfo?.data?.value ?? []),
          );
        } else {
          setEntityId(null);
          setKpiIntegrationData(null);
        }
      } catch (error) {
        setEntityId(null);
        setKpiIntegrationData(null);
      } finally {
        setIsLoading(false);
      }
    };

    if (goalId) {
      fetchFinancialEntity();
    }
  }, [goalId, refreshTrigger]);

  const refresh = () => {
    setTimeout(() => {
      _setRefreshTrigger((prev) => prev + 1);
    });
  };

  if (isLoading) {
    return (
      <div className="okr_financials_section" style={{ padding: '20px' }}>
        {t('Loading...')}
      </div>
    );
  }

  if (!entityId) {
    return (
      <div className="okr_financials_section" style={{ padding: '20px' }}>
        {t('No financial targets configured for this objective.')}
      </div>
    );
  }

  return (
    <>
      <div className="okr_financials_section">
        <FinancialsChartModal
          objectId={Number(goalId)}
          objectType={FinancialsObjectType.OKR}
          entityId={entityId}
          initialValues={financialsConfigureState}
          externalSourceId={kpiIntegrationData ? WOBO_APP_IDS.EXCEL : undefined}
          externalCostBudgetResources={externalCostBudgetResources}
          onTrackEvent={window.wb_track}
          trackingLocation={FINANCIALS_LOCATIONS.OKR_POPUP}
          onDelete={() => {
            if (kpiIntegrationData?._id) {
              KpiService.deactivateIntegration([kpiIntegrationData._id]);
            }
            setEntityId(null);
            setKpiIntegrationData(null);
            setExternalCostBudgetResources(undefined);
            // navigate to first tab
          }}
          financialConfigureModalRenderChildren={(props) => (
            <Box sx={{ position: 'relative', marginTop: '12px' }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  onClick={() => {
                    setShowExcelIntegrationModal(true);
                    setFinancialsConfigureState(
                      (props as unknown as { state?: FinancialsConfigureState })?.state,
                    );
                  }}
                  style={{
                    padding: '16px',
                    justifyContent: 'flex-start',
                    width: 'max-content',
                    fontSize: '13px',
                    fontWeight: 700,
                    borderRadius: '4px',
                    border: '0.5px solid #0072AB',
                    color: '#0072AB',
                  }}
                  startIcon={<ExcelIcon />}
                >
                  {kpiIntegrationData
                    ? t('Modify data connections')
                    : t('Automate values from external source')}
                </Button>
                {kpiIntegrationData && (
                  <Button
                    onClick={async () => {
                      if (!entityId || isSyncing) return;
                      setIsSyncing(true);
                      const financialsService = new FinancialsService();
                      await financialsService.resyncIntegration(entityId);
                      (props as { onRefetch?: () => void })?.onRefetch?.();
                      setExternalCostBudgetResources((prev) => (prev ? { ...prev } : {}));
                      refresh();
                      setIsSyncing(false);
                    }}
                    disabled={isSyncing}
                    style={{
                      padding: '16px',
                      justifyContent: 'flex-start',
                      width: 'max-content',
                      fontSize: '13px',
                      fontWeight: 700,
                      borderRadius: '4px',
                      border: '0.5px solid #0072AB',
                      color: '#0072AB',
                    }}
                  >
                    {t('Sync data from connection')}
                  </Button>
                )}
              </Box>
              <Backdrop open={isSyncing} sx={{ zIndex: 100001 }}>
                <CircularProgress color="inherit" />
              </Backdrop>
            </Box>
          )}
          isModal={false}
          baseUrl=""
          sx={{
            zIndex: 100000,
          }}
        />
      </div>
      {showExcelIntegrationModal && (
        <ExcelIntegrationModal
          open={showExcelIntegrationModal}
          onClose={() => setShowExcelIntegrationModal(false)}
          onSave={async (data: any) => {
            setKpiIntegrationData(data?.kpiData);
            if (entityId) {
              try {
                await resyncFinancialIntegration(entityId, () => {
                  refresh();
                });
                if (hasExcelIntegrationDataChanged(kpiIntegrationData, data?.kpiData)) {
                  trackFinancialsEvent(FINANCIALS_ACTIONS.EDIT_FINANCIALS, {
                    location: FINANCIALS_LOCATIONS.OKR_POPUP,
                    object: FINANCIALS_OBJECTS.OBJECTIVE,
                    source: FINANCIALS_SOURCES.EXCEL,
                  });
                }
              } finally {
                setShowExcelIntegrationModal(false);
                setExternalCostBudgetResources(
                  transformDataPointValues(data?.kpiData?.data?.value ?? []),
                );
              }
            } else {
              setShowExcelIntegrationModal(false);
            }
          }}
          onRemove={() => {
            setKpiIntegrationData(null);
            setShowExcelIntegrationModal(false);
            setExternalCostBudgetResources(undefined);
          }}
          getObjectIdAsync={async (
            dataPointValues: Array<{ id: number; value: number }>,
          ) => {
            const hasAnyCellSelected = dataPointValues.some(
              (dp) => dp.value != null,
            );
            if (!hasAnyCellSelected) {
              return null;
            }
            const financialsService = new FinancialsService();
            const externalData = transformDataPointValues(dataPointValues);
            const response = await financialsService.createEntity(
              FinancialsObjectType.OKR,
              Number(goalId),
              {
                ...financialsConfigureState,
                dataProviders: financialsConfigureState?.dataProviders ?? [],
                ...externalData,
              },
            );
            setExternalCostBudgetResources(externalData);
            refresh();

            return response?.data?.financial?.id;
          }}
          objectId={entityId}
          objectType="financial"
          updateFrequency="daily"
          config={{
            showAdvancedSection: false,
            dataPoints: [
              { id: FinancialType.Budget, label: t('Budget'), optional: true },
              { id: FinancialType.Resources, label: t('Resources'), optional: true },
              { id: FinancialType.Cost, label: t('Cost'), optional: true },
            ],
          }}
        />
      )}
      <style>
        {`
          .MuiModal-root.MuiDialog-root.AppServices-ExcelModal {
            z-index: ${100000 + 1000000};
          }
        `}
      </style>
    </>
  );
};

export default OkrFinancials;
